import React, { useState, useEffect } from 'react';

/* ── Button ─────────────────────────────────────────────────── */
export function Button({ children, variant = 'primary', loading, className = '', ...props }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: '8px', padding: '11px 22px', borderRadius: 'var(--r)',
    fontSize: '14px', fontWeight: '500', border: 'none',
    cursor: loading || props.disabled ? 'not-allowed' : 'pointer',
    opacity: props.disabled ? 0.6 : 1,
    transition: 'all 0.15s', width: props.fullWidth ? '100%' : undefined,
  };
  const variants = {
    primary:  { background: 'var(--accent)', color: '#fff' },
    dark:     { background: 'var(--ink)',    color: 'var(--paper)' },
    ghost:    { background: 'var(--white)',  color: 'var(--ink)', border: '1px solid var(--paper-3)' },
    danger:   { background: 'var(--red)',    color: '#fff' },
  };
  return (
    <button style={{ ...base, ...variants[variant] }} className={className} {...props}>
      {loading ? <Spinner size={16} color="#fff" /> : children}
    </button>
  );
}

/* ── Input ──────────────────────────────────────────────────── */
export function Input({ label, error, ...props }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      {label && (
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500',
          color: 'var(--ink-soft)', marginBottom: '6px' }}>
          {label}
        </label>
      )}
      <input
        style={{
          width: '100%', padding: '11px 14px',
          background: 'var(--white)',
          border: `1px solid ${error ? 'var(--red)' : 'var(--paper-3)'}`,
          borderRadius: 'var(--r)', fontFamily: 'var(--sans)',
          fontSize: '14px', color: 'var(--ink)', outline: 'none',
          transition: 'border-color 0.15s',
        }}
        {...props}
      />
      {error && (
        <p style={{ fontSize: '12px', color: 'var(--red)', marginTop: '4px' }}>{error}</p>
      )}
    </div>
  );
}

/* ── Spinner ─────────────────────────────────────────────────── */
export function Spinner({ size = 20, color = 'var(--accent)' }) {
  return (
    <div style={{
      width: size, height: size,
      border: `2px solid ${color}30`,
      borderTopColor: color,
      borderRadius: '50%',
      animation: 'spin 0.6s linear infinite',
      flexShrink: 0,
    }} />
  );
}

/* ── Toast ───────────────────────────────────────────────────── */
let _showToast = null;

export function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    _showToast = (msg, type = 'default') => {
      const id = Date.now();
      setToasts(t => [...t, { id, msg, type }]);
      setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
    };
  }, []);

  return (
    <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 999,
      display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          background: t.type === 'error' ? 'var(--red)' : 'var(--ink)',
          color: 'var(--paper)', padding: '12px 20px',
          borderRadius: 'var(--r)', fontSize: '13px', fontWeight: '500',
          animation: 'fadeUp 0.3s ease', boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          maxWidth: '320px',
        }}>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

export const showToast = (msg, type) => _showToast?.(msg, type);

/* ── Badge ───────────────────────────────────────────────────── */
export function Badge({ children, color = 'gray' }) {
  const colors = {
    gray:    { bg: 'var(--paper-2)', text: 'var(--ink-mute)' },
    green:   { bg: 'rgba(93,202,165,0.15)', text: 'var(--green)' },
    amber:   { bg: 'rgba(232,168,124,0.15)', text: '#9a5c2a' },
    red:     { bg: 'rgba(192,57,43,0.1)', text: 'var(--red)' },
    blue:    { bg: 'rgba(55,138,221,0.12)', text: '#185fa5' },
    coral:   { bg: 'rgba(200,86,58,0.1)', text: 'var(--accent)' },
    dark:    { bg: 'var(--ink)', text: 'var(--paper)' },
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{
      fontSize: '11px', fontWeight: '500', padding: '2px 8px',
      borderRadius: '100px', background: c.bg, color: c.text,
      letterSpacing: '0.02em', whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}

/* ── AnimatedDot ─────────────────────────────────────────────── */
export function AnimatedDot({ color = 'var(--teal)', delay = '0s', size = 7 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: color, flexShrink: 0,
      animation: `pulse 2s ease-in-out ${delay} infinite`,
    }} />
  );
}
